/**
 * Arkansas State-Specific Diagnostic Questions
 * 
 * REGULATORY FRAMEWORK:
 * Arkansas REQUIRES state licensure for home health agencies under Ark. Code Ann. § 20-10-801 et seq.
 * 
 * Applicable Regulations:
 * - Rules for Home Health Agencies in Arkansas (007.05.22-007 Ark. Code R.)
 * - Effective Date: June 17, 2024 (most recent revision)
 * 
 * Key Sections:
 * - Section 7: Application for License (Class A/B, annual renewal)
 * - Section 8: Inspections (at least every 3 years)
 * - Section 9: Denial, Suspension, Revocation of License
 * - Section 10: Training (HHA 75 hrs, PCA 40 hrs)
 * - Section 11: General Requirements (policies, personnel, patient rights, records)
 * - Section 12: Standards for Skilled Care Services
 * - Section 13: Standards for Extended Care Services
 * - Section 14: Standards for Personal Care Services
 * 
 * License Types:
 * - Class A: Medicare-certified agencies (must meet 42 CFR 484)
 * - Class B: Non-Medicare agencies
 * 
 * License Renewal: Annual (expires December 31)
 * Regulatory Body: Arkansas Department of Health (ADH), Health Facility Services
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const ARKANSAS_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'AR-A1',
    itemId: 'A1',
    stateCode: 'AR',
    title: 'ADH Home Health Agency License Status',
    description: 'Arkansas requires all home health agencies to be licensed by ADH under Ark. Code Ann. § 20-10-801',
    severity: 'high',
    regulationCode: '20-10-801',
    regulationName: 'License',
    agencyName: 'Arkansas Department of Health - Health Facility Services',
    agencyAbbr: 'ADH',
    agencyPhone: '(501) 661-2201',
    agencyUrl: 'https://www.healthy.arkansas.gov/programs-services/topics/home-health-agency-licensing',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Health Agency license from Arkansas ADH?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'HHA licenses expire December 31 annually and must be renewed each calendar year.',
      },
      {
        id: 'q2',
        question: 'What is your license classification?',
        inputType: 'select',
        options: [
          { value: 'class_a', label: 'Class A (Medicare-certified)' },
          { value: 'class_b', label: 'Class B (Non-Medicare)' },
          { value: 'unsure', label: 'Not sure' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'What is your home health agency license expiration date?',
        inputType: 'date',
        helpText: 'Arkansas HHA licenses expire December 31 each year (Section 7.D)',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'unsure', result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q3', value: 90, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q3', value: 45, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q3', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your ADH Home Health Agency license is current and active per Ark. Code Ann. § 20-10-801.',
      atRisk: 'Your HHA license expires within 90 days. Arkansas licenses expire December 31 annually. Submit renewal application immediately.',
      nonCompliant: 'Operating a home health agency in Arkansas without a valid ADH license violates Ark. Code Ann. § 20-10-801. Contact ADH immediately at (501) 661-2201.',
      unknown: 'Verify your HHA license status with ADH at (501) 661-2201.',
    },
    stateNotes: [
      'HHA licenses expire December 31 each year (Section 7.D)',
      'Class A = Medicare-certified, Class B = Non-Medicare',
      'ADH conducts inspections at least every 3 years (Section 8.C)',
      'Agencies must have a physical location within Arkansas (Section 5.A)',
      'Service area limited to 100 miles from parent/branch office',
    ],
  },

  {
    id: 'AR-A2',
    itemId: 'A2',
    stateCode: 'AR',
    title: 'Administrator Qualifications (Section 11.B.4/C)',
    description: 'Arkansas requires qualified administrators for home health agencies',
    severity: 'high',
    regulationCode: 'Section 11.B.4/C',
    regulationName: 'Administrator',
    agencyName: 'Arkansas Department of Health',
    agencyAbbr: 'ADH',
    agencyPhone: '(501) 661-2201',
    agencyUrl: 'https://www.healthy.arkansas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a qualified administrator who is a physician, RN, or has at least 1 year supervisory/administrative experience in home health or related health programs?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per Section 4.A and 11.B.4, administrator must be an agency employee.',
      },
      {
        id: 'q2',
        question: 'Is there an alternate administrator designated to act in the administrator\'s absence?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per Section 11.B.5 and 11.C.5',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Administrator meets Section 11.B.4/C requirements with alternate designated.',
      atRisk: 'Alternate administrator not designated per Section 11.B.5.',
      nonCompliant: 'No qualified administrator designated per Section 11.B.4.',
      unknown: 'Verify administrator qualifications per Section 11.',
    },
    stateNotes: [
      'Administrator must be physician, RN, or have 1+ year supervisory experience (Section 11.B.4)',
      'Administrator must be an agency employee (Section 4.A)',
      'Alternate administrator required per Section 11.B.5',
      'Administrator appointed by Governing Board (Section 11.B.4)',
    ],
  },

  {
    id: 'AR-A3',
    itemId: 'A3',
    stateCode: 'AR',
    title: 'Clinical Manager/Supervising RN (Section 4.E)',
    description: 'Arkansas requires a qualified clinical manager',
    severity: 'high',
    regulationCode: 'Section 4.E',
    regulationName: 'Clinical Manager',
    agencyName: 'Arkansas State Board of Nursing',
    agencyAbbr: 'ASBN',
    agencyPhone: '(501) 686-2700',
    agencyUrl: 'https://www.arsbn.org/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a clinical manager who is a licensed physician, PT, SLP, OT, audiologist, social worker, or RN?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per Section 4.E definition',
      },
      {
        id: 'q2',
        question: 'Is their Arkansas professional license current and verified?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical manager meets Section 4.E requirements.',
      atRisk: 'Verify professional license status.',
      nonCompliant: 'No clinical manager designated per Section 4.E.',
      unknown: 'Review clinical manager requirements.',
    },
    stateNotes: [
      'Clinical manager: physician, PT, SLP, OT, audiologist, social worker, or RN (Section 4.E)',
      'Must hold current Arkansas professional license',
      'Verify license at arsbn.org (nursing) or respective board',
    ],
  },

  {
    id: 'AR-A4',
    itemId: 'A4',
    stateCode: 'AR',
    title: 'Written Policies and Procedures (Section 11.A)',
    description: 'Arkansas requires specific written policies per Section 11.A',
    severity: 'high',
    regulationCode: 'Section 11.A',
    regulationName: 'Policies',
    agencyName: 'Arkansas Department of Health',
    agencyAbbr: 'ADH',
    agencyPhone: '(501) 661-2201',
    agencyUrl: 'https://www.healthy.arkansas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following Arkansas-required policies does your agency have documented? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'org_structure', label: 'Organizational structure with ownership and lines of authority (11.A.1.a)' },
          { value: 'services', label: 'Services offered (11.A.1.b)' },
          { value: 'hours', label: 'Hours of operation (11.A.1.c)' },
          { value: 'acceptance', label: 'Patient acceptance, referral, transfer, termination criteria (11.A.1.d)' },
          { value: 'budget', label: 'Annual operating budget approved by governing body (11.A.1.e)' },
          { value: 'orientation', label: 'Personnel orientation (11.A.2)' },
          { value: 'personnel', label: 'Personnel policies (11.A.3)' },
          { value: 'procedures', label: 'Procedures for all tasks and patient care (11.A.4)' },
          { value: 'condition_changes', label: 'Reporting changes in patient condition (11.A.5)' },
          { value: 'infection', label: 'Infection control program (11.A.6)' },
          { value: 'qapi', label: 'Quality Assurance and Improvement Program (11.A.8)' },
          { value: 'complaints', label: 'Complaints and incidents documentation (11.A.9)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 11, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 8, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 8, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your policy manual meets Arkansas Section 11.A requirements.',
      atRisk: 'Your policy manual is missing required sections per Section 11.A.',
      nonCompliant: 'Multiple required policies are missing per Section 11.A. ADH surveyors will cite incomplete policies.',
      unknown: 'Review your policies against Section 11.A requirements.',
    },
    stateNotes: [
      'Policies must be reviewed annually (Section 11.A)',
      'Personnel records requirements specified in Section 11.A.7',
    ],
  },

  {
    id: 'AR-A5',
    itemId: 'A5',
    stateCode: 'AR',
    title: 'Governing Body (Section 11.B)',
    description: 'Arkansas requires an organized governing body',
    severity: 'high',
    regulationCode: 'Section 11.B',
    regulationName: 'Governing Body',
    agencyName: 'Arkansas Department of Health',
    agencyAbbr: 'ADH',
    agencyPhone: '(501) 661-2201',
    agencyUrl: 'https://www.healthy.arkansas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have an organized Governing Board with at least one member (may be the owner)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does the Governing Board fulfill the following responsibilities? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'qa_plan', label: 'Approves quality assurance plan (11.B.1)' },
          { value: 'bylaws', label: 'Adopts and reviews written bylaws (11.B.2)' },
          { value: 'policies', label: 'Approves written policies and procedures (11.B.3)' },
          { value: 'admin', label: 'Appoints administrator who is agency employee (11.B.4)' },
          { value: 'alternate', label: 'Approves plan for alternate administrator (11.B.5)' },
          { value: 'fiscal', label: 'Oversees management and fiscal affairs (11.B.6)' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 3, result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Governing body structure meets Section 11.B requirements.',
      atRisk: 'Governing body responsibilities incomplete per Section 11.B.',
      nonCompliant: 'No governing body designated per Section 11.B.',
      unknown: 'Review governing body requirements.',
    },
    stateNotes: [
      'Governing Board may be as few as one member (owner) per Section 11.B',
      'Must approve QA plan, bylaws, policies, and administrator',
      'Oversees fiscal affairs and management (Section 11.B.6)',
    ],
  },

  {
    id: 'AR-A6',
    itemId: 'A6',
    stateCode: 'AR',
    title: 'QAPI Program (Section 11.A.8)',
    description: 'Arkansas requires a Quality Assurance and Improvement Program',
    severity: 'high',
    regulationCode: 'Section 11.A.8',
    regulationName: 'Quality Assurance',
    agencyName: 'Arkansas Department of Health',
    agencyAbbr: 'ADH',
    agencyPhone: '(501) 661-2201',
    agencyUrl: 'https://www.healthy.arkansas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have an agency-wide Quality Assurance and Improvement Program?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does your QAPI program include the following elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'surveys', label: 'Client satisfaction surveys (11.A.8.a)' },
          { value: 'supervision', label: 'Supervision of services (11.A.8.b)' },
          { value: 'inspections', label: 'Results of inspections, surveys, and audits (11.A.8.c)' },
          { value: 'pi', label: 'Performance improvement projects (11.A.8.d)' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 2, result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'QAPI program meets Section 11.A.8 requirements.',
      atRisk: 'QAPI program is incomplete per Section 11.A.8.',
      nonCompliant: 'No QAPI program exists. This violates Section 11.A.8.',
      unknown: 'Review QAPI requirements per Section 11.A.8.',
    },
    stateNotes: [
      'QAPI must include client satisfaction surveys (11.A.8.a)',
      'Supervision of services required (11.A.8.b)',
      'Must track inspection and audit results (11.A.8.c)',
      'Performance improvement projects required (11.A.8.d)',
    ],
  },

  {
    id: 'AR-A7',
    itemId: 'A7',
    stateCode: 'AR',
    title: 'Medicare/Medicaid Certification',
    description: 'Medicare and Arkansas Medicaid enrollment status',
    severity: 'high',
    regulationCode: '42 CFR 484',
    regulationName: 'Medicare CoP',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Is your agency Medicare certified (Class A license)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Class A agencies must meet 42 CFR 484 Conditions of Participation (Section 7.D.1)',
      },
      {
        id: 'q2',
        question: 'Is your agency enrolled as an Arkansas Medicaid provider?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Medicare and Medicaid certification active.',
      atRisk: 'Consider obtaining full certification to expand patient base.',
      nonCompliant: 'Missing certifications limit patient access.',
      unknown: 'Verify certification status.',
    },
    stateNotes: [
      'Class A license = Medicare-certified (must meet 42 CFR 484)',
      'Class B license = Non-Medicare agencies (Section 7.D)',
      'Arkansas Medicaid enrollment separate from Medicare certification',
    ],
  },

  {
    id: 'AR-A8',
    itemId: 'A8',
    stateCode: 'AR',
    title: 'Liability Insurance Coverage',
    description: 'Professional liability insurance requirements',
    severity: 'high',
    regulationCode: 'Industry Standard',
    regulationName: 'Insurance',
    agencyName: 'Arkansas Department of Health',
    agencyAbbr: 'ADH',
    agencyPhone: '(501) 661-2201',
    agencyUrl: 'https://www.healthy.arkansas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain professional liability insurance?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'What is your coverage amount per occurrence?',
        inputType: 'select',
        options: [
          { value: '1m_plus', label: '$1,000,000 or more' },
          { value: '500k', label: '$500,000 - $999,999' },
          { value: 'under_500k', label: 'Under $500,000' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: '1m_plus', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: '500k', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'under_500k', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Liability coverage is adequate.',
      atRisk: 'Consider increasing liability coverage.',
      nonCompliant: 'Operating without liability insurance creates significant risk.',
      unknown: 'Review insurance requirements.',
    },
    stateNotes: [
      'Industry standard: minimum $1,000,000 per occurrence',
      'Professional liability (malpractice) and general liability recommended',
      'Verify coverage includes home health services',
    ],
  },

  {
    id: 'AR-A9',
    itemId: 'A9',
    stateCode: 'AR',
    title: 'Workers Compensation Insurance',
    description: 'Arkansas workers compensation coverage',
    severity: 'high',
    regulationCode: '11-9-401',
    regulationName: 'Workers Compensation',
    agencyName: 'Arkansas Workers Compensation Commission',
    agencyAbbr: 'AWCC',
    agencyPhone: '(501) 682-3930',
    agencyUrl: 'https://www.awcc.state.ar.us/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have current workers compensation insurance?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Arkansas requires workers compensation for employers with 3+ employees.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Workers compensation coverage meets Arkansas requirements.',
      atRisk: 'Verify coverage status.',
      nonCompliant: 'Workers compensation required per Ark. Code Ann. § 11-9-401.',
      unknown: 'Verify with AWCC at (501) 682-3930.',
    },
    stateNotes: [
      'Arkansas requires workers comp for employers with 3+ employees',
      'Ark. Code Ann. § 11-9-401 governs requirements',
      'AWCC: (501) 682-3930 or awcc.state.ar.us',
    ],
  },

  {
    id: 'AR-A10',
    itemId: 'A10',
    stateCode: 'AR',
    title: 'Record Retention (Section 11.G)',
    description: 'Arkansas requires retention per state and federal law',
    severity: 'medium',
    regulationCode: 'Section 11.G.3',
    regulationName: 'Record Retention',
    agencyName: 'Arkansas Department of Health',
    agencyAbbr: 'ADH',
    agencyPhone: '(501) 661-2201',
    agencyUrl: 'https://www.healthy.arkansas.gov',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain patient records after discharge?',
        inputType: 'select',
        options: [
          { value: '10plus', label: '10 years or longer' },
          { value: '7to9', label: '7-9 years' },
          { value: '5to6', label: '5-6 years' },
          { value: 'less5', label: 'Less than 5 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Section 11.G.3 requires retention per state and federal laws. Medicaid requires 5 years minimum. Arkansas Medical Society recommends 10 years.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: '10plus', result: 'COMPLIANT' },
      { condition: 'includes', questionId: 'q1', value: ['7to9', '5to6'], result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['less5', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your record retention policy meets Arkansas requirements.',
      atRisk: 'Consider extending retention to 10 years (Arkansas Medical Society recommendation).',
      nonCompliant: 'Record retention does not meet minimum requirements. Medicaid requires 5 years; AMS recommends 10 years.',
      unknown: 'Review record retention requirements per Section 11.G.3.',
    },
    stateNotes: [
      'Section 11.G.3: "Records shall be retained according to State and Federal laws"',
      'Arkansas Medicaid requires 5 years minimum',
      'Arkansas Medical Society recommends 10 years',
      'Clinical notes must be incorporated into record every 14 days (Section 11.G.6)',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL FILES
  // ============================================
  {
    id: 'AR-B1',
    itemId: 'B1',
    stateCode: 'AR',
    title: 'Personnel File Requirements (Section 11.A.7)',
    description: 'Arkansas requires specific documents in each employee\'s personnel file',
    severity: 'high',
    regulationCode: 'Section 11.A.7',
    regulationName: 'Personnel Records',
    agencyName: 'Arkansas Department of Health',
    agencyAbbr: 'ADH',
    agencyPhone: '(501) 661-2201',
    agencyUrl: 'https://www.healthy.arkansas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following documents do you maintain in EVERY employee\'s personnel file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'job_desc', label: 'Job description signed by employee (11.A.7.a)' },
          { value: 'qualifications', label: 'Qualifications, education, and/or training (11.A.7.b)' },
          { value: 'application', label: 'Application for employment (11.A.7.c)' },
          { value: 'background', label: 'Criminal history checks per Ark. Code Ann. § 20-38-101 (11.A.7.d)' },
          { value: 'verification', label: 'Verification of licensure, permits, references, experience, education (11.A.7.e)' },
          { value: 'evaluations', label: 'Performance evaluations and disciplinary actions (11.A.7.f)' },
          { value: 'work_history', label: 'Verification of work history (11.A.7.g)' },
          { value: 'i9', label: 'I-9 Employment Eligibility Verification' },
          { value: 'tb', label: 'TB clearance documentation' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 8, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 6, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 6, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain all Arkansas-required documentation per Section 11.A.7.',
      atRisk: 'Some personnel files are missing required documents.',
      nonCompliant: 'Personnel files are missing critical documentation required by Section 11.A.7.',
      unknown: 'Audit personnel files against Section 11.A.7 requirements.',
    },
    stateNotes: [
      'Section 11.A.7 lists required personnel file documents',
      'Signed job description required (11.A.7.a)',
      'Criminal background check per Ark. Code Ann. § 20-38-101 (11.A.7.d)',
      'Work history verification required (11.A.7.g)',
    ],
  },

  {
    id: 'AR-B2',
    itemId: 'B2',
    stateCode: 'AR',
    title: 'License Verification (Section 11.A.7.e)',
    description: 'Arkansas requires verification of all professional licenses',
    severity: 'high',
    regulationCode: 'Section 11.A.7.e',
    regulationName: 'License Verification',
    agencyName: 'Arkansas State Board of Nursing',
    agencyAbbr: 'ASBN',
    agencyPhone: '(501) 686-2700',
    agencyUrl: 'https://www.arsbn.org/',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify all professional licenses (RN, LPN, PT, etc.) before employment?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always verified with licensing board' },
          { value: 'sometimes', label: 'Sometimes verified' },
          { value: 'no', label: 'No verification process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you document license verification in personnel files?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'sometimes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'License verification documented per Section 11.A.7.e.',
      atRisk: 'License verification inconsistent.',
      nonCompliant: 'Licenses not verified before employment.',
      unknown: 'Review license verification procedures.',
    },
    stateNotes: [
      'Section 11.A.7.e requires license verification',
      'Arkansas State Board of Nursing: arsbn.org',
      'Document verification in personnel file',
      'Track license expiration dates',
    ],
  },

  {
    id: 'AR-B3',
    itemId: 'B3',
    stateCode: 'AR',
    title: 'Work History Verification (Section 11.A.7.g)',
    description: 'Arkansas requires verification of work history',
    severity: 'medium',
    regulationCode: 'Section 11.A.7.g',
    regulationName: 'Work History',
    agencyName: 'Arkansas Department of Health',
    agencyAbbr: 'ADH',
    agencyPhone: '(501) 661-2201',
    agencyUrl: 'https://www.healthy.arkansas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify work history for all new hires?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is work history verification documented in personnel files?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Work history verification documented per Section 11.A.7.g.',
      atRisk: 'Work history verification documentation incomplete.',
      nonCompliant: 'Work history not verified per Section 11.A.7.g.',
      unknown: 'Review work history verification procedures.',
    },
    stateNotes: [
      'Section 11.A.7.g requires work history verification',
      'Contact previous employers to verify employment dates',
      'Document verification attempts in personnel file',
    ],
  },

  {
    id: 'AR-B4',
    itemId: 'B4',
    stateCode: 'AR',
    title: 'Performance Evaluations (Section 11.A.7.f)',
    description: 'Arkansas requires documented performance evaluations',
    severity: 'medium',
    regulationCode: 'Section 11.A.7.f',
    regulationName: 'Performance Evaluation',
    agencyName: 'Arkansas Department of Health',
    agencyAbbr: 'ADH',
    agencyPhone: '(501) 661-2201',
    agencyUrl: 'https://www.healthy.arkansas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct performance evaluations for all staff?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are evaluations documented in personnel files?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Performance evaluations documented per Section 11.A.7.f.',
      atRisk: 'Performance evaluation documentation incomplete.',
      nonCompliant: 'Performance evaluations required per Section 11.A.7.f.',
      unknown: 'Review performance evaluation requirements.',
    },
    stateNotes: [
      'Section 11.A.7.f requires documented performance evaluations',
      'Include disciplinary actions in personnel file',
      'Conduct evaluations at least annually',
    ],
  },

  // ============================================
  // SECTION C: BACKGROUND SCREENING
  // ============================================
  {
    id: 'AR-C1',
    itemId: 'C1',
    stateCode: 'AR',
    title: 'Criminal Background Check (Ark. Code Ann. § 20-38-101)',
    description: 'Arkansas requires criminal background checks for all direct care staff',
    severity: 'high',
    regulationCode: '20-38-101',
    regulationName: 'Background Check',
    agencyName: 'Arkansas State Police',
    agencyAbbr: 'ASP',
    agencyPhone: '(501) 618-8500',
    agencyUrl: 'https://dps.arkansas.gov/law-enforcement/arkansas-state-police/services-programs/criminal-background-checks/',
    questions: [
      {
        id: 'q1',
        question: 'For ALL direct care employees, which background checks do you have on file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'asp', label: 'Arkansas State Police criminal background check' },
          { value: 'fbi', label: 'FBI national fingerprint-based check' },
          { value: 'registry', label: 'Arkansas Nurse Aide Registry verification' },
          { value: 'sex_offender', label: 'Sex Offender Registry check (Ark. Code Ann. § 12-12-913)' },
          { value: 'maltreatment', label: 'Child/Adult Maltreatment Central Registry (Ark. Code Ann. § 12-18-901)' },
          { value: 'oig', label: 'OIG/SAM exclusion list check' },
        ],
        required: true,
        helpText: 'Per Section 11.A.7.d, criminal history checks required per Ark. Code Ann. § 20-38-101',
      },
      {
        id: 'q2',
        question: 'Are ALL background checks completed BEFORE any patient contact?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - always before patient contact' },
          { value: 'no', label: 'No - sometimes staff start early' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      // NON_COMPLIANT if staff start before clearance (direct violation of Ark. Code Ann. § 20-38-101)
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      // NON_COMPLIANT if missing required checks
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
      // COMPLIANT only if 5+ checks AND always complete before patient contact
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 5 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      // AT_RISK if 3-4 checks but not all required
      { condition: 'countBetween', questionId: 'q1', value: [3, 4], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Background check documentation complete per Ark. Code Ann. § 20-38-101.',
      atRisk: 'Background checks may be incomplete. Review all files and ensure all required checks are completed.',
      nonCompliant: 'VIOLATION: Staff are working before background clearance OR required checks are missing. Per Ark. Code Ann. § 20-38-101, no patient contact is permitted until all checks are completed.',
      unknown: 'Audit all files for background check documentation.',
    },
    stateNotes: [
      'Ark. Code Ann. § 20-38-101 requires criminal background checks',
      'ASP background check required: dps.arkansas.gov',
      'FBI fingerprint-based check required for federal healthcare programs',
      'No patient contact until background checks complete',
      'Sex Offender Registry check per Ark. Code Ann. § 12-12-913',
    ],
  },

  {
    id: 'AR-C2',
    itemId: 'C2',
    stateCode: 'AR',
    title: 'Nurse Aide Registry Check',
    description: 'Arkansas requires verification against the Nurse Aide Registry',
    severity: 'high',
    regulationCode: 'Section 11.A.7.e',
    regulationName: 'Aide Registry',
    agencyName: 'Arkansas Department of Health',
    agencyAbbr: 'ADH',
    agencyPhone: '(501) 661-2201',
    agencyUrl: 'https://www.healthy.arkansas.gov/programs-services/topics/nurse-aide-training',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify all potential aide hires against the Arkansas Nurse Aide Registry before employment?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, 100% verified before hire' },
          { value: 'most', label: 'Most verified, some start before' },
          { value: 'some', label: 'Some are not verified' },
          { value: 'unsure', label: 'Not sure how to check' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['some', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All potential hires verified against Arkansas Nurse Aide Registry.',
      atRisk: 'Registry verification inconsistent.',
      nonCompliant: 'Failing to verify Nurse Aide Registry violates Arkansas requirements.',
      unknown: 'Contact ADH at (501) 661-2201 for registry verification procedures.',
    },
    stateNotes: [
      'Nurse Aide Registry verification required before hire',
      'Check for findings of abuse, neglect, or misappropriation',
      'ADH maintains the Arkansas Nurse Aide Registry',
      'Contact: (501) 661-2201',
    ],
  },

  {
    id: 'AR-C3',
    itemId: 'C3',
    stateCode: 'AR',
    title: 'OIG Exclusion List Check',
    description: 'Federal exclusion list verification for Medicare/Medicaid billing',
    severity: 'high',
    regulationCode: '42 CFR 1001',
    regulationName: 'OIG Exclusion',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the OIG exclusion list (LEIE) before hiring?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'How often do you re-check for current employees?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Monthly' },
          { value: 'quarterly', label: 'Quarterly' },
          { value: 'annually', label: 'Annually' },
          { value: 'never', label: 'Only at hire' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'monthly' },
      ], result: 'COMPLIANT' },
      { condition: 'includes', questionId: 'q2', value: ['quarterly', 'annually'], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG exclusion checks completed at hire and monthly.',
      atRisk: 'OIG recommends monthly screening.',
      nonCompliant: 'OIG checks required. Billing for excluded individuals is Medicare fraud.',
      unknown: 'Review OIG exclusion check requirements.',
    },
    stateNotes: [
      'OIG LEIE check: exclusions.oig.hhs.gov',
      'SAM.gov exclusion check also recommended',
      'Monthly screening is federal best practice',
      'Billing for excluded individuals is Medicare/Medicaid fraud',
    ],
  },

  {
    id: 'AR-C4',
    itemId: 'C4',
    stateCode: 'AR',
    title: 'Adult Maltreatment Central Registry Check (A.C.A. § 12-12-1716)',
    description: 'Arkansas requires verification against the Adult and Long-Term Care Facility Resident Maltreatment Central Registry for employees providing care to vulnerable adults',
    severity: 'high',
    regulationCode: '12-12-1716',
    regulationName: 'Maltreatment Registry',
    agencyName: 'Arkansas Department of Human Services - Adult Protective Services',
    agencyAbbr: 'DHS-APS',
    agencyPhone: '1-800-482-8049',
    agencyUrl: 'https://humanservices.arkansas.gov/divisions-shared-services/aging-adult-behavioral-health-services/adult-protective-services/adult-maltreatment-registry-background-check-request/',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the Arkansas Adult Maltreatment Central Registry before hiring employees who will provide care to adults?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, for all prospective employees' },
          { value: 'some', label: 'Sometimes / inconsistent' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'The registry contains investigative determinations on all founded reports of adult maltreatment.',
      },
      {
        id: 'q2',
        question: 'Do you conduct re-checks on current employees?',
        inputType: 'select',
        options: [
          { value: 'every_5_years', label: 'Yes, at least every 5 years' },
          { value: 'occasionally', label: 'Occasionally' },
          { value: 'never', label: 'Only at hire' },
        ],
        helpText: 'Arkansas requires background checks at least once every 5 years.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'every_5_years' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'some' },
        { condition: 'equals', questionId: 'q2', value: 'occasionally' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Adult Maltreatment Central Registry checks completed per A.C.A. § 12-12-1716.',
      atRisk: 'Registry checks should be conducted for all hires and at least every 5 years for current employees.',
      nonCompliant: 'Failing to check the Adult Maltreatment Central Registry before hiring exposes your agency to liability. Employees with founded maltreatment findings should not provide care to vulnerable adults.',
      unknown: 'Contact DHS-APS at 1-800-482-8049 for registry check procedures.',
    },
    stateNotes: [
      'Fee: $10 per check (plus processing fees)',
      'Processing time: Allow 30 days from submission',
      'Forms must be notarized',
      'Re-checks required at least every 5 years',
      'Registry contains founded reports of abuse, neglect, exploitation, and sexual abuse',
      'Separate from Child Maltreatment Central Registry (also required)',
    ],
  },

  // ============================================
  // SECTION D: HEALTH & SAFETY
  // ============================================
  {
    id: 'AR-D1',
    itemId: 'D1',
    stateCode: 'AR',
    title: 'TB Screening (Section 11.A.6.c)',
    description: 'Arkansas requires TB screening per CDC guidelines',
    severity: 'high',
    regulationCode: 'Section 11.A.6.c',
    regulationName: 'TB Screening',
    agencyName: 'Arkansas Department of Health',
    agencyAbbr: 'ADH',
    agencyPhone: '(501) 661-2201',
    agencyUrl: 'https://www.healthy.arkansas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL direct care staff have current TB clearance on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff have current TB clearance' },
          { value: 'most', label: 'Most do, some may be expired' },
          { value: 'no', label: 'No / Not tracking this' },
        ],
        required: true,
        helpText: 'Per Section 11.A.6.c, TB prevention must conform to current CDC Guidelines.',
      },
      {
        id: 'q2',
        question: 'How do you track TB clearance expiration dates?',
        inputType: 'select',
        options: [
          { value: 'system', label: 'Automated system with reminders' },
          { value: 'manual', label: 'Manual tracking (spreadsheet, calendar)' },
          { value: 'none', label: 'No active tracking system' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'includes', questionId: 'q2', value: ['system', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'none' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'TB clearance current for all staff per Section 11.A.6.c.',
      atRisk: 'Some TB clearances may be expired.',
      nonCompliant: 'Staff working without valid TB clearance violates Section 11.A.6.c.',
      unknown: 'Verify TB clearance status per CDC guidelines.',
    },
    stateNotes: [
      'Section 11.A.6.c requires TB prevention per CDC guidelines',
      'Baseline screening at hire required',
      'IGRA blood test or TST acceptable',
      'Track expiration dates in personnel files',
    ],
  },

  {
    id: 'AR-D2',
    itemId: 'D2',
    stateCode: 'AR',
    title: 'Infection Control Program (Section 11.A.6)',
    description: 'Arkansas requires comprehensive infection control',
    severity: 'high',
    regulationCode: 'Section 11.A.6',
    regulationName: 'Infection Control',
    agencyName: 'Arkansas Department of Health',
    agencyAbbr: 'ADH',
    agencyPhone: '(501) 661-2201',
    agencyUrl: 'https://www.healthy.arkansas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your infection control program include the following? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'prevention', label: 'Goal of prevention and control of spread of infection (11.A.6.a)' },
          { value: 'work_restrictions', label: 'Work restrictions per CDC recommendations (11.A.6.b)' },
          { value: 'tb_prevention', label: 'TB prevention per current CDC guidelines (11.A.6.c)' },
          { value: 'hand_hygiene', label: 'Hand hygiene procedures' },
          { value: 'ppe', label: 'PPE guidelines' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do all staff receive infection control training?',
        inputType: 'select',
        options: [
          { value: 'initial_annual', label: 'Yes, during orientation and annually' },
          { value: 'initial_only', label: 'Yes, during orientation only' },
          { value: 'no', label: 'No formal training' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'initial_annual' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'initial_only', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection control program meets Section 11.A.6 requirements.',
      atRisk: 'Infection control training should be provided annually.',
      nonCompliant: 'Infection control program required per Section 11.A.6.',
      unknown: 'Review infection control requirements.',
    },
    stateNotes: [
      'Section 11.A.6 requires comprehensive infection control program',
      'Goal: prevention and control of infection spread (11.A.6.a)',
      'Work restrictions per CDC recommendations (11.A.6.b)',
      'TB prevention per CDC guidelines (11.A.6.c)',
    ],
  },

  {
    id: 'AR-D3',
    itemId: 'D3',
    stateCode: 'AR',
    title: 'Complaint and Incident Documentation (Section 11.A.9)',
    description: 'Arkansas requires documentation of complaints and incidents',
    severity: 'high',
    regulationCode: 'Section 11.A.9',
    regulationName: 'Incidents',
    agencyName: 'Arkansas Department of Health',
    agencyAbbr: 'ADH',
    agencyPhone: '(501) 661-2201',
    agencyUrl: 'https://www.healthy.arkansas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain records of complaints and incidents per Section 11.A.9?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does your documentation include all required elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'names', label: 'Names of involved individuals (11.A.9.a)' },
          { value: 'relationship', label: 'Relationship to patient (11.A.9.b)' },
          { value: 'nature', label: 'Nature of complaint/incident (11.A.9.c)' },
          { value: 'date', label: 'Date of complaint/incident (11.A.9.d)' },
          { value: 'action', label: 'Action taken including referrals (11.A.9.e)' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q2', value: 5, result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Complaint/incident documentation meets Section 11.A.9 requirements.',
      atRisk: 'Complaint/incident documentation incomplete.',
      nonCompliant: 'Complaint and incident records required per Section 11.A.9.',
      unknown: 'Review complaint documentation requirements.',
    },
    stateNotes: [
      'Section 11.A.9 lists required documentation elements',
      'Must include names, relationship, nature, date, and action taken',
      'Include referrals made (11.A.9.e)',
      'Maintain records for QAPI review',
    ],
  },

  {
    id: 'AR-D4',
    itemId: 'D4',
    stateCode: 'AR',
    title: 'PPE and Safety Equipment',
    description: 'Personal protective equipment requirements',
    severity: 'medium',
    regulationCode: '29 CFR 1910.132',
    regulationName: 'PPE',
    agencyName: 'Arkansas Department of Health',
    agencyAbbr: 'ADH',
    agencyPhone: '(501) 661-2201',
    agencyUrl: 'https://www.healthy.arkansas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide appropriate PPE to all direct care staff?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you train staff on proper PPE use?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'PPE provision and training adequate.',
      atRisk: 'PPE training incomplete.',
      nonCompliant: 'PPE required for direct care staff.',
      unknown: 'Review PPE requirements.',
    },
    stateNotes: [
      'OSHA 29 CFR 1910.132 governs PPE requirements',
      'Employer must provide PPE at no cost to employees',
      'Training on proper use required',
      'Include gloves, masks, gowns, eye protection as appropriate',
    ],
  },

  // ============================================
  // SECTION E: TRAINING & COMPETENCY
  // ============================================
  {
    id: 'AR-E1',
    itemId: 'E1',
    stateCode: 'AR',
    title: 'Home Health Aide Training (Section 10.A)',
    description: 'Arkansas requires 75 hours of training for home health aides',
    severity: 'high',
    regulationCode: 'Section 10.A',
    regulationName: 'HHA Training',
    agencyName: 'Arkansas Department of Health',
    agencyAbbr: 'ADH',
    agencyPhone: '(501) 661-2201',
    agencyUrl: 'https://www.healthy.arkansas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL new home health aides complete the following training requirements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: '75_hours', label: 'At least 75 hours classroom and supervised practical training (10.A.1.b)' },
          { value: '16_classroom', label: 'Minimum 16 hours classroom before practical (10.A.1.b)' },
          { value: '16_practical', label: 'Minimum 16 hours supervised practical training (10.A.1.b)' },
          { value: 'rn_supervision', label: 'Training under direct RN supervision (10.A.1.a)' },
          { value: 'table2_topics', label: 'Covers all topics in Table 2 (10.A.2)' },
          { value: 'competency', label: 'Competency evaluation upon completion' },
        ],
        required: true,
        helpText: 'Per Section 10.A, HHA training requires 75 hours with specific structure.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HHA training meets Section 10.A requirements (75 hours).',
      atRisk: 'HHA training may be incomplete per Section 10.A.',
      nonCompliant: 'HHAs providing services without required 75-hour training violates Section 10.A.',
      unknown: 'Review HHA training requirements per Section 10.A.',
    },
    stateNotes: [
      'Total 75 hours minimum (Section 10.A.1.b)',
      '16 hours classroom BEFORE 16 hours practical',
      'Training must address Table 2 topics',
      'Training must be approved by ADH (Section 10.C)',
    ],
  },

  {
    id: 'AR-E2',
    itemId: 'E2',
    stateCode: 'AR',
    title: 'Personal Care Aide Training (Section 10.B)',
    description: 'Arkansas requires 40 hours of training for personal care aides',
    severity: 'high',
    regulationCode: 'Section 10.B',
    regulationName: 'PCA Training',
    agencyName: 'Arkansas Department of Health',
    agencyAbbr: 'ADH',
    agencyPhone: '(501) 661-2201',
    agencyUrl: 'https://www.healthy.arkansas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide personal care aide services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do ALL personal care aides complete required training? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: '40_hours', label: 'Minimum 40 hours classroom and clinical (10.B.3)' },
          { value: 'table1_topics', label: 'Covers all topics in Table 1 (10.B.1)' },
          { value: 'rn_supervision', label: 'Supervised by RN (10.B.2)' },
          { value: 'objectives', label: 'Written course objectives (10.B.4.a)' },
          { value: 'outcomes', label: 'Written expected outcomes (10.B.4.b)' },
          { value: 'evaluation', label: 'Written method of evaluation (10.B.4.c)' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'PCA training meets Section 10.B requirements (40 hours).',
      atRisk: 'PCA training may be incomplete per Section 10.B.',
      nonCompliant: 'PCAs providing services without required 40-hour training violates Section 10.B.',
      unknown: 'Review PCA training requirements per Section 10.B.',
    },
    stateNotes: [
      'Total 40 hours minimum (Section 10.B.3)',
      'Must include classroom and clinical instruction',
      'Training must address Table 1 topics',
      'Training must be approved by ADH (Section 10.C)',
    ],
  },

  {
    id: 'AR-E3',
    itemId: 'E3',
    stateCode: 'AR',
    title: 'HHA Competency Evaluation (Section 12.E.2)',
    description: 'Arkansas requires competency evaluation for all home health aides',
    severity: 'high',
    regulationCode: 'Section 12.E.2',
    regulationName: 'Competency',
    agencyName: 'Arkansas Department of Health',
    agencyAbbr: 'ADH',
    agencyPhone: '(501) 661-2201',
    agencyUrl: 'https://www.healthy.arkansas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct competency evaluations for all HHAs that include RN observation of required skills?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per Section 12.E.2, RN must observe aide performing skills with a patient.',
      },
      {
        id: 'q2',
        question: 'Does the competency evaluation address all required skills? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'communication', label: 'Communication skills (12.E.2.a)' },
          { value: 'vitals', label: 'Temperature, pulse, respiration (12.E.2.b)' },
          { value: 'hygiene', label: 'Personal hygiene and grooming techniques (12.E.2.c)' },
          { value: 'transfer', label: 'Safe transfer techniques and ambulation (12.E.2.d)' },
          { value: 'rom', label: 'Range of motion and positioning (12.E.2.e)' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q2', value: 5, result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Competency evaluations meet Section 12.E.2 requirements.',
      atRisk: 'Competency evaluation may be incomplete.',
      nonCompliant: 'Competency evaluations required per Section 12.E.2.',
      unknown: 'Review competency evaluation requirements.',
    },
    stateNotes: [
      'Section 12.E.2 lists required competency skills',
      'RN must observe aide performing skills with patient',
      'Includes communication, vitals, hygiene, transfer, ROM (12.E.2.a-e)',
      'Document competency evaluation in personnel file',
    ],
  },

  {
    id: 'AR-E4',
    itemId: 'E4',
    stateCode: 'AR',
    title: 'Annual In-Service Training (Section 12.E.8c)',
    description: 'Arkansas requires 12 hours of annual in-service training',
    severity: 'high',
    regulationCode: 'Section 12.E.8c/14.B.5',
    regulationName: 'Annual Training',
    agencyName: 'Arkansas Department of Health',
    agencyAbbr: 'ADH',
    agencyPhone: '(501) 661-2201',
    agencyUrl: 'https://www.healthy.arkansas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL aides complete 12 hours of annual in-service training?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all aides complete 12+ hours annually' },
          { value: 'most', label: 'Most complete required hours, some are behind' },
          { value: 'no', label: 'No structured annual training program' },
        ],
        required: true,
        helpText: 'Per Section 12.E.8c and 14.B.5, 12 hours minimum annually.',
      },
      {
        id: 'q2',
        question: 'Does annual training address patient care aspects of the aide\'s job?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Training must "address areas that directly relate to the patient care aspects of the aide\'s job"',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Annual training meets Arkansas requirements (12 hours per year).',
      atRisk: 'Some aides may be behind on annual training.',
      nonCompliant: 'No structured annual training program. Section 12.E.8c requires 12 hours annually.',
      unknown: 'Review annual training requirements.',
    },
    stateNotes: [
      '12 hours minimum annually (Section 12.E.8c and 14.B.5)',
      'Must address patient care aspects of aide job',
      'Track training hours per employee',
      'Document in personnel file',
    ],
  },

  {
    id: 'AR-E5',
    itemId: 'E5',
    stateCode: 'AR',
    title: 'Mandatory Abuse Reporting Training (A.C.A. § 12-12-1703 / § 12-18-402)',
    description: 'Arkansas requires training on mandatory reporting for both adult maltreatment (A.C.A. § 12-12-1701 et seq.) and child maltreatment (A.C.A. § 12-18-402)',
    severity: 'high',
    regulationCode: '12-12-1703/12-18-402',
    regulationName: 'Mandated Reporting',
    agencyName: 'Arkansas Department of Human Services - Adult Protective Services',
    agencyAbbr: 'DHS-APS',
    agencyPhone: '1-800-482-8049',
    agencyUrl: 'https://humanservices.arkansas.gov/divisions-shared-services/aging-adult-behavioral-health-services/adult-protective-services/mandated-reporters-of-adult-maltreatment/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL direct care staff receive training on mandatory abuse reporting under Arkansas law?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff receive training' },
          { value: 'most', label: 'Most staff receive training' },
          { value: 'no', label: 'No / not sure' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'When do staff receive abuse reporting training?',
        inputType: 'select',
        options: [
          { value: 'orientation_annual', label: 'During orientation and annually' },
          { value: 'orientation_only', label: 'During orientation only' },
          { value: 'no', label: 'No formal training' },
        ],
      },
      {
        id: 'q3',
        question: 'Does training cover BOTH adult and child maltreatment reporting requirements?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - both adult and child reporting covered' },
          { value: 'no', label: 'No - only one or neither' },
        ],
        helpText: 'Home care staff may encounter both vulnerable adults and children in the home.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'orientation_annual' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'orientation_only' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All staff trained on mandatory abuse reporting per A.C.A. § 12-12-1703 (adults) and § 12-18-402 (children).',
      atRisk: 'Abuse reporting training should be provided annually and cover both adult and child maltreatment.',
      nonCompliant: 'Failure to train staff on mandatory abuse reporting violates Arkansas law. Knowingly failing to report is a Class B misdemeanor.',
      unknown: 'Verify all staff receive abuse reporting training covering both adult and child maltreatment.',
    },
    stateNotes: [
      'Adult Maltreatment Hotline: 1-800-482-8049 (A.C.A. § 12-12-1701 et seq.)',
      'Child Maltreatment Hotline: 1-800-482-8988 (A.C.A. § 12-18-402)',
      'Mandated reporters must provide their identifying information when reporting',
      'Failure to report: Class B misdemeanor (A.C.A. § 12-12-1720)',
      'Reporter identity is kept confidential by law',
    ],
  },

  {
    id: 'AR-E6',
    itemId: 'E6',
    stateCode: 'AR',
    title: 'CPR/First Aid Certification',
    description: 'Emergency response training for direct care staff',
    severity: 'medium',
    regulationCode: 'Industry Standard',
    regulationName: 'Emergency Training',
    agencyName: 'Arkansas Department of Health',
    agencyAbbr: 'ADH',
    agencyPhone: '(501) 661-2201',
    agencyUrl: 'https://www.healthy.arkansas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do all direct care staff have current CPR certification?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff have current CPR' },
          { value: 'most', label: 'Most staff, some may be expired' },
          { value: 'no', label: 'No / Not required' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you track CPR certification expiration dates?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'CPR certifications current for all direct care staff.',
      atRisk: 'Some CPR certifications may be expired.',
      nonCompliant: 'Staff lacking CPR certification.',
      unknown: 'Review CPR certification requirements.',
    },
    stateNotes: [
      'CPR certification recommended for all direct care staff',
      'AHA, Red Cross, or equivalent providers accepted',
      'Certification typically valid for 2 years',
      'Track expiration dates and schedule recertification',
    ],
  },

  // ============================================
  // SECTION F: CARE DELIVERY
  // ============================================
  {
    id: 'AR-F1',
    itemId: 'F1',
    stateCode: 'AR',
    title: 'Plan of Care (Section 12.B.3)',
    description: 'Arkansas requires written plans of care for each patient',
    severity: 'high',
    regulationCode: 'Section 12.B.3',
    regulationName: 'Plan of Care',
    agencyName: 'Arkansas Department of Health',
    agencyAbbr: 'ADH',
    agencyPhone: '(501) 661-2201',
    agencyUrl: 'https://www.healthy.arkansas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written plan of care for EVERY patient at admission?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always at admission' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal care plan process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do your care plans include all required elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'services', label: 'Potential services to be rendered (12.B.3.c)' },
          { value: 'frequency', label: 'Frequency of visits and/or hours of service (12.B.3.c)' },
          { value: 'providers', label: 'Assignment of health care providers (12.B.3.c)' },
          { value: 'duration', label: 'Estimated length of services (12.B.3.c)' },
          { value: 'individualized', label: 'Individualized to patient needs (12.B.3.e)' },
          { value: 'physician_sig', label: 'Physician/practitioner signature within 30 days (12.B.3.f)' },
        ],
      },
      {
        id: 'q3',
        question: 'Are care plans revised at least every 60 days per Section 12.B.3.d?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Care plans meet Section 12.B.3 requirements.',
      atRisk: 'Care plans may be incomplete or not created at admission.',
      nonCompliant: 'Services provided without written care plans violates Section 12.B.3.',
      unknown: 'Review care plan requirements per Section 12.B.3.',
    },
    stateNotes: [
      'Care plans developed with patient/family at admission (12.B.3.a)',
      'Revised at least every 60 days (12.B.3.d)',
      'Physician signature required within 30 days (12.B.3.f)',
    ],
  },

  {
    id: 'AR-F2',
    itemId: 'F2',
    stateCode: 'AR',
    title: 'Initial Assessment (Section 12.B.2)',
    description: 'Arkansas requires initial assessment within 48 hours',
    severity: 'high',
    regulationCode: 'Section 12.B.2.a',
    regulationName: 'Assessment',
    agencyName: 'Arkansas Department of Health',
    agencyAbbr: 'ADH',
    agencyPhone: '(501) 661-2201',
    agencyUrl: 'https://www.healthy.arkansas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Are initial assessments completed within 48 hours of referral/discharge per Section 12.B.2.a?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always within 48 hours' },
          { value: 'usually', label: 'Usually' },
          { value: 'no', label: 'No / Not tracking' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is the initial assessment completed by an RN (if nursing services provided) or other skilled professional?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Initial assessments meet Section 12.B.2 requirements (within 48 hours).',
      atRisk: 'Initial assessments may not consistently meet 48-hour requirement.',
      nonCompliant: 'Initial assessments not completed within 48 hours per Section 12.B.2.a.',
      unknown: 'Review initial assessment requirements.',
    },
    stateNotes: [
      'Initial assessment within 48 hours of referral/discharge (12.B.2.a)',
      'RN must complete assessment if nursing services provided',
      'Assessment forms basis for care plan development',
    ],
  },

  {
    id: 'AR-F3',
    itemId: 'F3',
    stateCode: 'AR',
    title: 'HHA Supervision (Section 12.E.8)',
    description: 'Arkansas requires RN supervision of home health aides every 14 days',
    severity: 'high',
    regulationCode: 'Section 12.E.8.a',
    regulationName: 'HHA Supervision',
    agencyName: 'Arkansas Department of Health',
    agencyAbbr: 'ADH',
    agencyPhone: '(501) 661-2201',
    agencyUrl: 'https://www.healthy.arkansas.gov',
    questions: [
      {
        id: 'q1',
        question: 'How often does a qualified healthcare professional conduct supervisory visits to patients receiving HHA services?',
        inputType: 'select',
        options: [
          { value: 'every_14_days', label: 'Every 14 days or more frequently' },
          { value: 'every_30', label: 'Every 30 days' },
          { value: 'rarely', label: 'Less frequently' },
          { value: 'never', label: 'No formal supervision schedule' },
        ],
        required: true,
        helpText: 'Section 12.E.8.a requires visit to patient\'s residence to update aide assignment sheet every 14 days for HHAs.',
      },
      {
        id: 'q2',
        question: 'Is the aide assignment sheet updated at each supervisory visit?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'every_14_days' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'every_30', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['rarely', 'never'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HHA supervision meets Section 12.E.8.a requirements (every 14 days).',
      atRisk: 'Supervision frequency does not meet 14-day requirement.',
      nonCompliant: 'HHAs not receiving required supervision per Section 12.E.8.a.',
      unknown: 'Review supervision requirements per Section 12.E.8.',
    },
    stateNotes: [
      'HHA supervision: every 14 days (Section 12.E.8.a)',
      'PCA supervision: every 60 days (Section 12.E.8.b)',
      'Aide assignment sheet must be updated at each visit',
    ],
  },

  {
    id: 'AR-F4',
    itemId: 'F4',
    stateCode: 'AR',
    title: 'PCA Supervision (Section 12.E.8.b)',
    description: 'Arkansas requires supervision of personal care aides every 60 days',
    severity: 'high',
    regulationCode: 'Section 12.E.8.b',
    regulationName: 'PCA Supervision',
    agencyName: 'Arkansas Department of Health',
    agencyAbbr: 'ADH',
    agencyPhone: '(501) 661-2201',
    agencyUrl: 'https://www.healthy.arkansas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide personal care aide services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'How often does a qualified professional conduct supervisory visits for patients receiving PCA services?',
        inputType: 'select',
        options: [
          { value: 'every_60_days', label: 'Every 60 days or more frequently' },
          { value: 'every_90', label: 'Every 90 days' },
          { value: 'annually', label: 'Annually' },
          { value: 'never', label: 'No formal supervision' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Section 12.E.8.b requires visit every 60 days for PCAs.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'every_60_days', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'every_90', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q2', value: ['annually', 'never'], result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'PCA supervision meets Section 12.E.8.b requirements (every 60 days).',
      atRisk: 'PCA supervision frequency does not meet 60-day requirement.',
      nonCompliant: 'PCAs not receiving required supervision per Section 12.E.8.b.',
      unknown: 'Review PCA supervision requirements.',
    },
    stateNotes: [
      'PCA supervision: every 60 days (Section 12.E.8.b)',
      'Less frequent than HHA supervision (14 days)',
      'Supervisory visit to patient residence',
      'Document supervision in clinical record',
    ],
  },

  {
    id: 'AR-F5',
    itemId: 'F5',
    stateCode: 'AR',
    title: 'Patient Rights (Section 11.E)',
    description: 'Arkansas requires providing patients with Bill of Rights',
    severity: 'high',
    regulationCode: 'Section 11.E',
    regulationName: 'Patient Rights',
    agencyName: 'Arkansas Department of Health',
    agencyAbbr: 'ADH',
    agencyPhone: '(501) 661-2201',
    agencyUrl: 'https://www.healthy.arkansas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide each patient with a written Bill of Rights per Section 11.E.1?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does your Bill of Rights include these required elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'services', label: 'Right to be informed of services (11.E.1.a)' },
          { value: 'participate', label: 'Right to participate in care plan (11.E.1.b)' },
          { value: 'responsibilities', label: 'Explanation of patient responsibilities (11.E.1.c)' },
          { value: 'contact', label: 'Agency contact information (11.E.1.d)' },
          { value: 'complaints', label: 'Complaint process and State Hotline number (11.E.1.e)' },
          { value: 'charges', label: 'Information about charges (11.E.1.f)' },
          { value: 'privacy', label: 'Right to privacy and confidentiality (11.E.1.g-h)' },
          { value: 'access_records', label: 'Right to access own records (11.E.1.i)' },
          { value: 'notice', label: 'Right to notice of termination/transfer (11.E.1.j)' },
          { value: 'grievances', label: 'Right to voice grievances (11.E.1.k)' },
          { value: 'control_access', label: 'Right to control access to home (11.E.1.l)' },
          { value: 'free_abuse', label: 'Right to be free from abuse (11.E.1.m)' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 10 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q2', value: 10, result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Patient rights notification meets Section 11.E requirements.',
      atRisk: 'Bill of Rights may be missing required elements.',
      nonCompliant: 'Written Bill of Rights required per Section 11.E.',
      unknown: 'Review patient rights requirements.',
    },
    stateNotes: [
      'Section 11.E.1 lists 12 required Bill of Rights elements',
      'Must include state complaint hotline number (11.E.1.e)',
      'Provide written copy to each patient',
      'Right to be free from abuse (11.E.1.m)',
    ],
  },

  {
    id: 'AR-F6',
    itemId: 'F6',
    stateCode: 'AR',
    title: 'Advance Directives (Section 11.F)',
    description: 'Arkansas requires advance directive policies and information',
    severity: 'medium',
    regulationCode: 'Section 11.F',
    regulationName: 'Advance Directives',
    agencyName: 'Arkansas Department of Health',
    agencyAbbr: 'ADH',
    agencyPhone: '(501) 661-2201',
    agencyUrl: 'https://www.healthy.arkansas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have written policies and procedures regarding advance directives per Section 11.F.1?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you provide written information about advance directives on the initial evaluation visit?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per Section 11.F.2',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Advance directive procedures meet Section 11.F requirements.',
      atRisk: 'Advance directive information should be provided at initial visit.',
      nonCompliant: 'Advance directive policies required per Section 11.F.',
      unknown: 'Review advance directive requirements.',
    },
    stateNotes: [
      'Written policies required per Section 11.F.1',
      'Provide written info at initial evaluation visit (11.F.2)',
      'Document acknowledgment in patient record',
    ],
  },

  {
    id: 'AR-F7',
    itemId: 'F7',
    stateCode: 'AR',
    title: 'Electronic Visit Verification (EVV)',
    description: 'EVV required for Medicaid-funded personal care services',
    severity: 'high',
    regulationCode: '21st Century Cures Act',
    regulationName: 'EVV',
    agencyName: 'Arkansas Department of Human Services',
    agencyAbbr: 'DHS',
    agencyPhone: '(501) 682-8292',
    agencyUrl: 'https://humanservices.arkansas.gov/divisions-shared-services/medical-services/evv/',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide Arkansas Medicaid-funded personal care services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - private pay only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you use a DHS-approved EVV system to document visits?',
        inputType: 'select',
        options: [
          { value: 'yes_compliant', label: 'Yes, fully implemented and compliant' },
          { value: 'yes_partial', label: 'Yes, but not for all visits' },
          { value: 'no', label: 'No EVV system in place' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes_compliant', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes_partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'EVV system properly implemented for Arkansas Medicaid services.',
      atRisk: 'EVV not used consistently for all Medicaid visits.',
      nonCompliant: 'Arkansas Medicaid personal care services require EVV per federal law.',
      unknown: 'If you provide Medicaid services, verify EVV compliance with DHS.',
    },
    stateNotes: [
      'EVV required under 21st Century Cures Act',
      'Arkansas DHS administers EVV program',
      'Required for Medicaid-funded personal care services',
      'Contact DHS: (501) 682-8292 or humanservices.arkansas.gov',
    ],
  },

  // ============================================
  // SECTION G: DOCUMENTATION
  // ============================================
  {
    id: 'AR-G1',
    itemId: 'G1',
    stateCode: 'AR',
    title: 'Clinical Records (Section 11.G)',
    description: 'Arkansas requires comprehensive clinical record documentation',
    severity: 'high',
    regulationCode: 'Section 11.G',
    regulationName: 'Records',
    agencyName: 'Arkansas Department of Health',
    agencyAbbr: 'ADH',
    agencyPhone: '(501) 661-2201',
    agencyUrl: 'https://www.healthy.arkansas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do your clinical records include all required elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'assessments', label: 'Assessments and evaluations (11.G.4.a)' },
          { value: 'plans', label: 'Plans and assignments (11.G.4.b)' },
          { value: 'advance_directives', label: 'Acknowledgment of advance directive info (11.G.4.c)' },
          { value: 'time', label: 'Date and time employees are in the home (11.G.4.d)' },
          { value: 'tasks', label: 'Tasks completed (11.G.4.e)' },
          { value: 'orders', label: 'Physician/practitioner orders if applicable (11.G.5.a)' },
          { value: 'supervision', label: 'Records of supervisory visits (11.G.5.b)' },
          { value: 'clinical_notes', label: 'Clinical notes (11.G.5.d)' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are clinical notes written the day service is rendered and incorporated into the record every 14 days per Section 11.G.6?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 7 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 5, result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Clinical records meet Section 11.G requirements.',
      atRisk: 'Clinical notes should be incorporated every 14 days per Section 11.G.6.',
      nonCompliant: 'Clinical records missing required documentation.',
      unknown: 'Review clinical record requirements per Section 11.G.',
    },
    stateNotes: [
      'Section 11.G lists required clinical record elements',
      'Notes written day of service, incorporated every 14 days (11.G.6)',
      'Include assessments, plans, time in home, tasks completed',
      'Supervisory visit records required (11.G.5.b)',
    ],
  },

  {
    id: 'AR-G2',
    itemId: 'G2',
    stateCode: 'AR',
    title: 'HIPAA Compliance',
    description: 'HIPAA-compliant handling of protected health information',
    severity: 'high',
    regulationCode: '45 CFR 164',
    regulationName: 'HIPAA',
    agencyName: 'U.S. Department of Health and Human Services',
    agencyAbbr: 'HHS-OCR',
    agencyPhone: '(800) 368-1019',
    agencyUrl: 'https://www.hhs.gov/hipaa/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have written HIPAA policies and procedures?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do all staff receive HIPAA training?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HIPAA compliance program meets requirements.',
      atRisk: 'HIPAA training may be incomplete.',
      nonCompliant: 'Written HIPAA policies required.',
      unknown: 'Review HIPAA compliance requirements.',
    },
    stateNotes: [
      '45 CFR 164 governs HIPAA requirements',
      'Written policies and procedures required',
      'All staff must receive HIPAA training',
      'Designate Privacy Officer',
    ],
  },

  {
    id: 'AR-G3',
    itemId: 'G3',
    stateCode: 'AR',
    title: 'Document Expiration Tracking',
    description: 'System to track license, certification, and clearance expirations',
    severity: 'high',
    regulationCode: 'Section 11.A.7',
    regulationName: 'Record Maintenance',
    agencyName: 'Arkansas Department of Health',
    agencyAbbr: 'ADH',
    agencyPhone: '(501) 661-2201',
    agencyUrl: 'https://www.healthy.arkansas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you track expiration dates for all required documents?',
        inputType: 'checklist',
        options: [
          { value: 'agency_license', label: 'Agency license (expires Dec 31 annually)' },
          { value: 'insurance', label: 'Liability insurance' },
          { value: 'tb', label: 'TB clearances' },
          { value: 'cpr', label: 'CPR/First Aid certifications' },
          { value: 'background', label: 'Background check updates' },
          { value: 'training', label: 'Annual training due dates (12 hrs)' },
          { value: 'nursing', label: 'RN/LPN licenses' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you receive advance reminders before documents expire?',
        inputType: 'select',
        options: [
          { value: 'auto', label: 'Yes, automated reminders 30+ days in advance' },
          { value: 'manual', label: 'Yes, manual review monthly' },
          { value: 'no', label: 'No proactive tracking' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 6 },
        { condition: 'includes', questionId: 'q2', value: ['auto', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Document expiration tracking is comprehensive.',
      atRisk: 'Expiration tracking is incomplete.',
      nonCompliant: 'No systematic document tracking in place.',
      unknown: 'Implement document expiration tracking system.',
    },
    stateNotes: [
      'Agency license expires December 31 annually',
      'Track RN/LPN license expirations',
      'Background checks at least every 5 years',
      '12-hour annual training due dates per employee',
    ],
  },

  {
    id: 'AR-G4',
    itemId: 'G4',
    stateCode: 'AR',
    title: 'Billing Accuracy',
    description: 'Clinical documentation supports billed services',
    severity: 'high',
    regulationCode: '42 CFR 424',
    regulationName: 'Billing',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do clinical notes support all billed services?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, documentation always supports billing' },
          { value: 'usually', label: 'Usually' },
          { value: 'inconsistent', label: 'Inconsistent' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you conduct periodic billing audits?',
        inputType: 'select',
        options: [
          { value: 'quarterly', label: 'Yes, at least quarterly' },
          { value: 'annually', label: 'Annually' },
          { value: 'never', label: 'No audits' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'includes', questionId: 'q2', value: ['quarterly', 'annually'] },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'inconsistent', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Billing documentation is accurate and audited regularly.',
      atRisk: 'Improve documentation consistency and implement regular audits.',
      nonCompliant: 'Documentation does not support billing. This creates fraud risk.',
      unknown: 'Review billing documentation requirements.',
    },
    stateNotes: [
      '42 CFR 424 governs billing documentation',
      'Clinical notes must support all billed services',
      'Quarterly audits recommended',
      'False Claims Act applies to inaccurate billing',
    ],
  },
];

export default ARKANSAS_QUESTIONS;